"use strict";
const carouselSelector = '.js-carousel';
const chunkContainerSelector = '.js-carousel_chunks';
const chunkSelector = '.js-carousel_chunk';
const previousButtonSelector = '.js-carousel_previous';
const nextButtonSelector = '.js-carousel_next';
const markersContainerSelector = '.js-carousel_markers';
const markersSelector = '.js-carousel_marker';
const resizeProbeSelector = '.js-carousel_resize_probe';
const columnsProperty = '--carousel-sliding-columns';
window.up.compiler(carouselSelector, element => {
    const chunkContainer = element.querySelector(chunkContainerSelector);
    const chunks = Array.from(element.querySelectorAll(chunkSelector));
    const previousButton = element.querySelector(previousButtonSelector);
    const nextButton = element.querySelector(nextButtonSelector);
    const markersContainer = element.querySelector(markersContainerSelector);
    const markers = Array.from(element.querySelectorAll(markersSelector));
    const resizeProbe = element.querySelector(resizeProbeSelector);
    const firstChunk = chunks[0];
    if (!chunkContainer || !firstChunk || chunks.length < 2 || !previousButton || !nextButton) {
        return;
    }
    // A sliding carousel shows several chunks at once and how many fit depends on the
    // viewport, so it hides its controls while everything fits and sizes its height to
    // the tallest chunk in view.
    const sliding = element.getAttribute('data-variant') === 'sliding';
    let activeIndex = 0;
    let isTicking = false;
    let layoutWidth = -1;
    const chunkScrollLeft = (chunk) => {
        return chunk.offsetLeft - firstChunk.offsetLeft;
    };
    const nearestChunkIndex = (scrollLeft) => {
        return chunks.reduce((nearest, chunk, index) => {
            const distance = Math.abs(chunkScrollLeft(chunk) - scrollLeft);
            if (distance < nearest.distance) {
                return { distance, index };
            }
            return nearest;
        }, { distance: Number.POSITIVE_INFINITY, index: 0 }).index;
    };
    const maxScrollLeft = () => {
        return chunkContainer.scrollWidth - chunkContainer.clientWidth;
    };
    // Scrolling is clamped at maxScrollLeft, so the chunk that ends up nearest to it is
    // the last one the buttons can reach, even if later chunks are already in view.
    const lastReachableIndex = () => {
        return nearestChunkIndex(maxScrollLeft());
    };
    const updateControls = () => {
        if (sliding) {
            const overflowing = maxScrollLeft() > 1;
            previousButton.hidden = !overflowing;
            nextButton.hidden = !overflowing;
            if (markersContainer) {
                markersContainer.hidden = !overflowing;
            }
        }
        const lastIndex = lastReachableIndex();
        previousButton.disabled = activeIndex === 0;
        nextButton.disabled = activeIndex >= lastIndex;
        const activeMarkerIndex = Math.min(activeIndex, markers.length - 1);
        markers.forEach((marker, index) => {
            if (sliding) {
                marker.hidden = index > lastIndex;
            }
            if (index === activeMarkerIndex) {
                marker.setAttribute('aria-current', 'page');
            }
            else {
                marker.removeAttribute('aria-current');
            }
        });
    };
    // Only whole chunks are shown: when their minimum width keeps the column count
    // from the stylesheet from fitting, the count is lowered and the chunks grow.
    const fitColumns = () => {
        chunkContainer.style.removeProperty(columnsProperty);
        const style = getComputedStyle(chunkContainer);
        const maxColumns = Number.parseInt(style.getPropertyValue(columnsProperty), 10) || 1;
        const gap = Number.parseFloat(style.columnGap) || 0;
        for (let columns = maxColumns; columns > 1; columns--) {
            chunkContainer.style.setProperty(columnsProperty, String(columns));
            const rowWidth = firstChunk.getBoundingClientRect().width * columns + gap * (columns - 1);
            if (rowWidth <= chunkContainer.clientWidth + 1) {
                return;
            }
        }
        chunkContainer.style.setProperty(columnsProperty, '1');
    };
    // The chunks stretch to the container height, so they are measured unstretched.
    const fitHeightToVisibleChunks = () => {
        const visibleStart = chunkContainer.scrollLeft;
        const visibleEnd = visibleStart + chunkContainer.clientWidth;
        const visibleChunks = chunks.filter(chunk => {
            const start = chunkScrollLeft(chunk);
            return start < visibleEnd - 1 && start + chunk.offsetWidth > visibleStart + 1;
        });
        if (visibleChunks.length === 0) {
            return;
        }
        chunkContainer.style.alignItems = 'flex-start';
        const height = Math.max(...visibleChunks.map(chunk => chunk.offsetHeight));
        chunkContainer.style.alignItems = '';
        chunkContainer.style.height = `${height}px`;
    };
    const update = () => {
        // A hidden carousel (e.g. the plan period an up-switch is not showing) has no
        // layout to measure: fitting it would pin a single column that then shows for a
        // frame once it becomes visible. It is measured as soon as it is shown instead.
        if (chunkContainer.clientWidth === 0) {
            return;
        }
        if (sliding && chunkContainer.clientWidth !== layoutWidth) {
            layoutWidth = chunkContainer.clientWidth;
            fitColumns();
        }
        activeIndex = nearestChunkIndex(chunkContainer.scrollLeft);
        updateControls();
        if (sliding) {
            fitHeightToVisibleChunks();
        }
    };
    const scheduleUpdate = () => {
        if (isTicking) {
            return;
        }
        isTicking = true;
        window.requestAnimationFrame(() => {
            update();
            isTicking = false;
        });
    };
    const scrollToChunk = (index) => {
        if (!chunks[index]) {
            return;
        }
        chunkContainer.scrollTo({ left: chunkScrollLeft(chunks[index]), behavior: 'smooth' });
    };
    const onPreviousClick = () => {
        scrollToChunk(Math.max(activeIndex - 1, 0));
    };
    const onNextClick = () => {
        scrollToChunk(Math.min(activeIndex + 1, lastReachableIndex()));
    };
    const onMarkerClick = (_event, _element, dataset) => {
        const markerIndex = Number(dataset.markerIndex);
        if (!Number.isNaN(markerIndex)) {
            scrollToChunk(markerIndex);
        }
    };
    window.up.on(previousButton, 'click', undefined, onPreviousClick);
    window.up.on(nextButton, 'click', undefined, onNextClick);
    window.up.on(chunkContainer, 'scroll', { passive: true }, scheduleUpdate);
    markers.forEach(marker => window.up.on(marker, 'click', undefined, onMarkerClick));
    if (sliding && resizeProbe) {
        update();
        // Updating synchronously in the observer callback, before the browser paints,
        // keeps a carousel that was just shown or resized from painting once with its
        // previous layout. The zero-height probe is observed instead of the chunks
        // because the update changes the chunks' height, and changing the observed
        // element's size inside its own callback is reported as a resize loop error.
        const resizeObserver = new ResizeObserver(update);
        resizeObserver.observe(resizeProbe);
        element.querySelectorAll('img').forEach(image => window.up.on(image, 'load', undefined, scheduleUpdate));
        document.fonts.ready.then(scheduleUpdate);
        return () => resizeObserver.disconnect();
    }
});
